import React from "react";
import { Box, type BoxProps } from "@chakra-ui/react";
import { type Tone, toneForRatio, toneTokens } from "./tones";

export interface MeterProps extends BoxProps {
  value: number;
  max: number;
  /** Fixed tone, or "auto" (default): green ≤ 80 %, amber ≤ 100 %, red above. "brand" = leaf.500. */
  tone?: Tone | "auto";
  /** Track width (default 56px; contract period uses 120px). */
  w?: BoxProps["w"];
  /** Accessible description, e.g. "12 of 30 days". */
  label: string;
}

/**
 * 4px track + fill. Fill is clamped to 100 % of the track; the tone still reflects the true ratio.
 * @example <Meter value={Math.min(days, 30)} max={30} tone="success" w="56px" label={`${days} of 30 days`} />
 */
export const Meter: React.FC<MeterProps> = ({ value, max, tone = "auto", w = "56px", label, ...rest }) => {
  const ratio = max > 0 && Number.isFinite(value) ? value / max : 0;
  const resolvedTone: Tone = tone === "auto" ? toneForRatio(ratio) : tone;
  const fill = `${Math.max(0, Math.min(1, ratio)) * 100}%`;
  return (
    // Spans (display: block) so the meter is valid inside buttons and table-cell text runs.
    <Box
      as="span"
      display="block"
      role="img"
      aria-label={label}
      title={label}
      w={w}
      h="4px"
      bg="sand.200"
      borderRadius="full"
      overflow="hidden"
      flexShrink={0}
      {...rest}
    >
      <Box as="span" display="block" h="full" w={fill} bg={toneTokens(resolvedTone).solid} borderRadius="full" transition="width 360ms cubic-bezier(0.2,0.8,0.2,1)" />
    </Box>
  );
};
